import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import apiClient from '@/api-client';
import { logger } from '@/lib/default-logger';
import { toast } from '@/components/core/toaster';
import { callLogsKeys } from './use-call-logs';
import { campaignsKeys } from './use-campaigns';

export interface Agent {
  id: string;
  name: string;
  prompt: string;
  flow: {
    name: string;
  };
  services: {
    type: string;
    plugin: {
      id: string;
    };
    settings?: any;
  }[];
  status: "active" | "inactive";
  settings: {
    [key: string]: any;
  };
  testUrl: string;
}

export interface Flow {
  name: string;
  services: string[];
  title: string;
  fields: any[];
}

export const agentsKeys = {
  all: ['agents'] as const,
  lists: () => [...agentsKeys.all, 'list'] as const,
  list: (query?: string) => [...agentsKeys.lists(), { query }] as const,
  details: () => [...agentsKeys.all, 'detail'] as const,
  detail: (id: string) => [...agentsKeys.details(), id] as const,
};

/**
 * The endpoint requires limit and offset and answers 400 without them, so calling this with
 * no query used to fail every time — leaving callers with an empty list and no error to
 * follow. Defaulting them makes the no-argument call behave the way its signature implies.
 */
export function useAgentsList(query?: string) {
  return useQuery({
    queryKey: agentsKeys.list(query),
    queryFn: async () => {
      const response = await apiClient.get(`/agents?${query || 'limit=100&offset=0'}`);
      return response.data;
    },
  });
}

export function useAgent(id: string) {
  return useQuery({
    queryKey: agentsKeys.detail(id),
    queryFn: async () => {
      const response = await apiClient.get(`/agents/${id}`);
      return response.data;
    },
    enabled: !!id,
  });
}

export function useDeleteAgent() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (id: string) => {
      const response = await apiClient.delete(`/agents/${id}`);
      return response.data;
    },
    onSuccess: (_, id) => {
      queryClient.removeQueries({ queryKey: agentsKeys.detail(id) });
      queryClient.invalidateQueries({ queryKey: agentsKeys.lists() });
      queryClient.removeQueries({ queryKey: callLogsKeys.all });
      queryClient.removeQueries({ queryKey: campaignsKeys.all });
      toast.success('Agent deleted successfully');
    },
    onError: (error: any) => {
      logger.error(error);
      const errorMessage = error?.response?.data?.message;
      toast.error(Array.isArray(errorMessage) ? errorMessage[0] : errorMessage || 'Agent deletion failed');
    },
  });
}
